import { ModuleRepository } from '../repository/module.repository';
import { LessonRepository } from '../repository/lesson.repository';
import { QuizRepository } from '../repository/quiz.repository';
import { CourseRepository } from '../repository/course.repository';
import {
  CreateModuleData,
  GetModuleData,
  CreateLessonData,
  GetLessonData,
  CreateQuizData,
  GetQuizData,
  GetCourseData,
  GetEnrollmentData
} from '../repository/interfaces';
import { IModuleUseCase } from './interfaces';

/**
 * Module Use Case
 * Manages the relationship between Modules, Lessons, and Quizzes
 * Takes three repository instances as requested by user
 * Handles complex module-lesson-quiz workflows
 */
export class ModuleUseCase implements IModuleUseCase {
  constructor(
    private moduleRepo: ModuleRepository,
    private lessonRepo: LessonRepository,
    private quizRepo: QuizRepository,
    private courseRepo: CourseRepository
  ) {
    // Removed BaseUseCase
  }

  /**
   * Create a complete module with lessons and quizzes
   */
  async createCompleteModule(
    moduleData: CreateModuleData,
    lessons: CreateLessonData[],
    quizzes: CreateQuizData[]
  ): Promise<{
    module: GetModuleData;
    lessons: GetLessonData[];
    quizzes: GetQuizData[];
  }> {
    try {
      // Create the module first
      const certificationModule = await this.moduleRepo.createModule(moduleData);

      // Create lessons for this module
      const createdLessons: GetLessonData[] = [];
      for (let i = 0; i < lessons.length; i++) {
        const lesson = await this.lessonRepo.createLesson({
          ...lessons[i],
          moduleId: certificationModule.id,
          courseId: certificationModule.courseId,
          orderIndex: i + 1
        });
        createdLessons.push(lesson);
      }

      // Create quizzes for this module
      const createdQuizzes: GetQuizData[] = [];
      for (let i = 0; i < quizzes.length; i++) {
        const quiz = await this.quizRepo.createQuiz({
          ...quizzes[i],
          courseId: certificationModule.courseId,
          lessonId: createdLessons[i]?.id // Link to corresponding lesson if available
        });
        createdQuizzes.push(quiz);
      }

      return {
        module: certificationModule,
        lessons: createdLessons,
        quizzes: createdQuizzes
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get module with all its lessons and quizzes
   */
  async getModuleWithContent(
    moduleId: string,
    userId?: string
  ): Promise<{
    module: GetModuleData;
    lessons: GetLessonData[];
    quizzes: GetQuizData[];
    course: GetCourseData;
    userProgress?: {
      completedLessons: number;
      totalLessons: number;
      quizScores: Array<{
        quizId: string;
        score: number;
        passed: boolean;
        attempts: number;
      }>;
    };
  }> {
    try {
      // Get module
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      // Get course
      const course = await this.courseRepo.getCourseById(certificationModule.courseId);
      if (!course) {
        throw new Error('Course not found');
      }

      // Get lessons and quizzes
      const lessons = await this.moduleRepo.getModuleLessons(moduleId);
      const quizzes = await this.moduleRepo.getModuleQuizzes(moduleId);

      let userProgress;
      if (userId) {
        // Calculate user progress (simplified)
        let completedLessons = 0;
        const quizScores = [];

        for (const quiz of quizzes) {
          const attempts = await this.quizRepo.getQuizAttemptsByUser(userId, quiz.id);
          if (attempts.length > 0) {
            const bestAttempt = attempts.reduce((best: any, current: any) =>
              current.score > best.score ? current : best
            );
            quizScores.push({
              quizId: quiz.id,
              score: bestAttempt.score,
              passed: bestAttempt.score >= (quiz.passingScore || 70),
              attempts: attempts.length
            });
          }
        }

        // For now, assume lessons are completed if quizzes are passed
        completedLessons = quizScores.filter(q => q.passed).length;

        userProgress = {
          completedLessons,
          totalLessons: lessons.length,
          quizScores
        };
      }

      return {
        module: certificationModule,
        lessons,
        quizzes,
        course,
        userProgress
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Add lesson to existing module
   */
  async addLessonToModule(
    moduleId: string,
    lessonData: Omit<CreateLessonData, 'moduleId' | 'courseId' | 'orderIndex'>,
    creatorId: string
  ): Promise<{
    lesson: GetLessonData;
    module: GetModuleData;
  }> {
    try {
      // Verify module exists and user has permission
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      // Get course to verify ownership
      const course = await this.courseRepo.getCourseById(certificationModule.courseId);
      if (!course) {
        throw new Error('Course not found');
      }

      if (course.instructorId !== creatorId) {
        throw new Error('Unauthorized: You do not own this course');
      }

      // Get current lessons to determine order
      const existingLessons = await this.moduleRepo.getModuleLessons(moduleId);
      const nextOrder = existingLessons.length + 1;

      // Create lesson
      const lesson = await this.lessonRepo.createLesson({
        ...lessonData,
        moduleId: moduleId,
        courseId: certificationModule.courseId,
        orderIndex: nextOrder
      });

      return {
        lesson,
        module: certificationModule
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Add quiz to existing module
   */
  async addQuizToModule(
    moduleId: string,
    quizData: Omit<CreateQuizData, 'courseId' | 'lessonId'>,
    creatorId: string
  ): Promise<{
    quiz: GetQuizData;
    module: GetModuleData;
  }> {
    try {
      // Verify module exists and user has permission
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      // Get course to verify ownership
      const course = await this.courseRepo.getCourseById(certificationModule.courseId);
      if (!course) {
        throw new Error('Course not found');
      }

      if (course.instructorId !== creatorId) {
        throw new Error('Unauthorized: You do not own this course');
      }

      // Create quiz
      const quiz = await this.quizRepo.createQuiz({
        ...quizData,
        courseId: certificationModule.courseId
      });

      return {
        quiz,
        module: certificationModule
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Reorder module content (lessons and quizzes)
   */
  async reorderModuleContent(
    moduleId: string,
    lessonOrder: any[],
    quizOrder: any[],
    creatorId: string
  ): Promise<any> {
    try {
      // Verify module ownership
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      const course = await this.courseRepo.getCourseById(certificationModule.courseId);
      if (course?.instructorId !== creatorId) {
        throw new Error('Unauthorized: You do not own this course');
      }

      // Update lesson order
      for (const item of lessonOrder) {
        await this.lessonRepo.updateLesson(item.lessonId, {
          orderIndex: item.orderIndex
        });
      }

      // Update quiz order (if quiz repository supports orderIndex)
      // Note: This would need to be implemented in quiz repository if needed

      // Get updated content
      const lessons = await this.moduleRepo.getModuleLessons(moduleId);
      const quizzes = await this.moduleRepo.getModuleQuizzes(moduleId);

      return {
        lessons,
        quizzes
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get module navigation (previous/next modules)
   */
  async getModuleNavigation(
    moduleId: string,
    userId?: string
  ): Promise<{
    current: GetModuleData;
    previous?: GetModuleData;
    next?: GetModuleData;
    course: GetCourseData;
    canAccess: boolean;
    reason?: string;
  }> {
    try {
      // Get current module
      const current = await this.moduleRepo.getModuleById(moduleId);
      if (!current) {
        throw new Error('Module not found');
      }

      // Get course
      const course = await this.courseRepo.getCourseById(current.courseId);
      if (!course) {
        throw new Error('Course not found');
      }

      // Check access permission
      let canAccess = true;
      let reason: string | undefined;

      if (userId) {
        // Check if user is enrolled
        const enrollments = await this.courseRepo.getUserEnrollments(userId);
        const isEnrolled = enrollments.some(
          (e: GetEnrollmentData) => e.courseId === course.id
        );

        if (!isEnrolled && course.price && course.price > 0) {
          canAccess = false;
          reason = 'Enrollment required';
        }
      }

      // Get previous and next modules
      const previous = await this.moduleRepo.getPreviousModule(moduleId);
      const next = await this.moduleRepo.getNextModule(moduleId);

      return {
        current,
        previous: previous || undefined,
        next: next || undefined,
        course,
        canAccess,
        reason
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Delete module and all its content
   */
  async deleteModuleWithContent(
    moduleId: string,
    creatorId: string
  ): Promise<{
    deletedLessons: number;
    deletedQuizzes: number;
    module: GetModuleData;
  }> {
    try {
      // Verify ownership
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      const course = await this.courseRepo.getCourseById(certificationModule.courseId);
      if (course?.instructorId !== creatorId) {
        throw new Error('Unauthorized: You do not own this course');
      }

      // Get content counts before deletion
      const lessons = await this.moduleRepo.getModuleLessons(moduleId);
      const quizzes = await this.moduleRepo.getModuleQuizzes(moduleId);

      // Delete quizzes first (to avoid foreign key constraints)
      for (const quiz of quizzes) {
        await this.quizRepo.deleteQuiz(quiz.id);
      }

      // Delete lessons
      for (const lesson of lessons) {
        await this.lessonRepo.deleteLesson(lesson.id);
      }

      // Delete module
      await this.moduleRepo.deleteModule(moduleId);

      return {
        deletedLessons: lessons.length,
        deletedQuizzes: quizzes.length,
        module: certificationModule
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Get module completion status for a user
   */
  async getModuleCompletionStatus(
    moduleId: string,
    userId: string
  ): Promise<{
    module: GetModuleData;
    completionPercentage: number;
    completedLessons: number;
    totalLessons: number;
    quizResults: Array<{
      quiz: GetQuizData;
      bestScore: number;
      passed: boolean;
      attempts: number;
    }>;
    canProceedToNext: boolean;
    nextModule?: GetModuleData;
  }> {
    try {
      // Get module and its content
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      const lessons = await this.moduleRepo.getModuleLessons(certificationModule.id);
      const quizzes = await this.moduleRepo.getModuleQuizzes(certificationModule.id);

      // Calculate lesson completion (simplified - would need actual completion tracking)
      const completedLessons = Math.floor(lessons.length * 0.8); // Mock 80% completion
      const completionPercentage = lessons.length > 0 ? (completedLessons / lessons.length) * 100 : 100;

      // Get quiz results
      const quizResults = [];
      for (const quiz of quizzes) {
        const attempts = await this.quizRepo.getQuizAttemptsByUser(userId, quiz.id);
        if (attempts.length > 0) {
          const bestAttempt = attempts.reduce((best, current) =>
            current.score > best.score ? current : best
          );
          quizResults.push({
            quiz,
            bestScore: bestAttempt.score,
            passed: bestAttempt.score >= (quiz.passingScore || 70),
            attempts: attempts.length
          });
        } else {
          quizResults.push({
            quiz,
            bestScore: 0,
            passed: false,
            attempts: 0
          });
        }
      }

      // Check if user can proceed (all quizzes passed)
      const allQuizzesPassed = quizResults.every(result => result.passed);
      const canProceedToNext = completionPercentage >= 100 && allQuizzesPassed;

      // Get next module
      const nextModule = canProceedToNext ? await this.moduleRepo.getNextModule(moduleId) : undefined;

      return {
        module: certificationModule,
        completionPercentage,
        completedLessons,
        totalLessons: lessons.length,
        quizResults,
        canProceedToNext,
        nextModule
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }

  /**
   * Bulk update module content
   */
  async bulkUpdateModuleContent(
    moduleId: string,
    updates: {
      lessons?: Array<{ id: string; data: Partial<CreateLessonData> }>;
      quizzes?: Array<{ id: string; data: Partial<CreateQuizData> }>;
      module?: Partial<CreateModuleData>;
    },
    creatorId: string
  ): Promise<{
    module?: GetModuleData;
    updatedLessons: GetLessonData[];
    updatedQuizzes: GetQuizData[];
  }> {
    try {
      // Verify ownership
      const certificationModule = await this.moduleRepo.getModuleById(moduleId);
      if (!certificationModule) {
        throw new Error('Module not found');
      }

      const course = await this.courseRepo.getCourseById(certificationModule.courseId);
      if (course?.instructorId !== creatorId) {
        throw new Error('Unauthorized: You do not own this course');
      }

      const updatedLessons: GetLessonData[] = [];
      const updatedQuizzes: GetQuizData[] = [];
      let updatedModule: GetModuleData | undefined;

      // Update module if provided
      if (updates.module) {
        updatedModule = await this.moduleRepo.updateModule(moduleId, updates.module);
      }

      // Update lessons
      if (updates.lessons) {
        for (const lessonUpdate of updates.lessons) {
          const updated = await this.lessonRepo.updateLesson(lessonUpdate.id, lessonUpdate.data);
          updatedLessons.push(updated);
        }
      }

      // Update quizzes
      if (updates.quizzes) {
        for (const quizUpdate of updates.quizzes) {
          const updated = await this.quizRepo.updateQuiz(quizUpdate.id, quizUpdate.data);
          updatedQuizzes.push(updated);
        }
      }

      return {
        module: updatedModule,
        updatedLessons,
        updatedQuizzes
      };
    } catch (error) { console.error("Error:", error); throw error; }
  }
}
